import { HttpHeaders } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { BrandingConfigurationService } from '@ptl/brand';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES, AxwayTokenFetcherService } from '@ptl/xhr';
import { PlansStateStructure } from 'src/app/angular-elements/plan-selection/configs/interfaces/plansStateStructure.interface';
import { PlanAttributes, SummaryView } from 'src/app/angular-elements/plan-selection/interfaces/plan-attributes.interface';
import { Product } from 'src/app/angular-elements/plan-selection/interfaces/product.interface';
import { PlanUtilityService } from 'src/app/angular-elements/plan-selection/plan-summary-list/plan-utility.service';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { ZipCountyStateStructure } from '../../interfaces/zipCountyStateStructure.interface';
import { EffectiveDateStateStructure } from '../configs/interfaces/effective-date.state-structure.interface';
import { API_ENDPOINTS } from 'src/app/shared/constants/api-endpoints.constant';

@Component({
  selector: 'app-sbc-navigation',
  templateUrl: './sbc-navigation.component.html',
  styleUrls: ['./sbc-navigation.component.scss'],
  standalone: false
})
export class SbcNavigationComponent implements OnInit {
  @Input() sbcType: string;
  @Input() sbcLabel: string;

  url: string;
  selectedPlan: Product<PlanAttributes, SummaryView>;

  constructor(
    private brandService: BrandingConfigurationService,
    private httpClient: HTTPWrapperContext,
    private reducerUtilityService: ReducerUtilityService,
    private planUtilityService: PlanUtilityService,
    private axwayTokenFetcherService: AxwayTokenFetcherService
  ) { }

  ngOnInit() {
    const planState: PlansStateStructure = this.reducerUtilityService.getState('Plans');
    const plansFromState = this.planUtilityService.getPlansByProductGroupAsArray();

    let curBrandCode = this.brandService.getCurrentBrand().cookieBrandCode?.toUpperCase();

    try {
      this.selectedPlan = plansFromState.filter(plan =>
        plan.attributes.PLAN_ID === planState.planIdForEnrollment)[0];
    } catch (e) {
      console.error('No plan selected: ', e);
    }

    if (this.sbcType === 'cmcrstFunction') {
      this.getSbcForCmcrstService();
    } else if (this.sbcType === 'aemContentPath') {
      this.generateSBCLinkWithAemPath();
    } else if (this.sbcType === 'cmodContentPath') {
       // Assuming generateSBCLink() returns a Promise that resolves to a Blob
      this.generateSBCLink().then((blobResponse) => {
        if (blobResponse instanceof Blob) {
          this.url = this.createBlobUrl(blobResponse);
        } else {
          this.url = null; // Or handle other non-Blob responses as needed
        } 
      });
   }                    
  }       

  getSbcForCmcrstService() {

    this.httpClient.setStrategy(XHR_STRATEGIES.DATAPOWER_MIDDLEWARE);
    const superProductId = this.selectedPlan.attributes.SUPER_PRODUCT_ID;

    const effectiveDateState: EffectiveDateStateStructure = this.reducerUtilityService.getReducerFromApplicationState('EffectiveDate');

    const sbcPostData = {
      "baseMessage": { "headerVersion": "1", "returnCode": null, "returnCodeDescription": null, "statusMsgs": null, "payloadVersion": "1" },
      "payload": {
        "productType": "I",
        "effectiveDate": `01/01/${effectiveDateState.effectiveDateYear}`,
        "productIds": [superProductId]
      }
    };

    const host = window.location.hostname;
    let env = '';
    if (host.indexOf('env') >= 0) 
        env = 't' + host.substring(host.indexOf('env'), host.indexOf('.'));

    const requestConfigImplementation = new RequestConfigImplementation({
      url: this.axwayTokenFetcherService.buildApiUrl(API_ENDPOINTS.DOCUMENT_INFO.NAS_SBC_DOC_SEARCH, {}),
      method: REQUEST_METHODS.POST,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded(),
        'x-hmhs-test-backend': `wasinter${env}.highmark.com`
      }),
      POST_OR_PUT_PAYLOAD: sbcPostData,
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS_V4);
    this.httpClient.makeRequest(requestConfigImplementation).then(async (sbcData: any) => {
        const fileName =
          sbcData.payload.products[0].NonErisaSbcDoc.fileName;
        const requestConfig = new RequestConfigImplementation({
          method: REQUEST_METHODS.GET,
          strategyConfigs: { forceModernUrl: true },
          headers: new HttpHeaders({
            'x-hmhs-test-backend': `wasinter${env}.highmark.com`
          }), 
          url: this.axwayTokenFetcherService.buildApiUrl(`${API_ENDPOINTS.DOCUMENT_INFO.NAS_SBC_DOC_RETRIEVAL}/${fileName}`, {}),
          responseType: 'blob',
          accept: 'application/pdf'
        });

        this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS_V4);

        try {
          const blob = await this.httpClient.makeRequest(requestConfig) as Blob;
          if (blob instanceof Blob) {
            this.url = this.createBlobUrl(blob);
          } else {
            this.url = null; // Or handle other non-Blob responses as needed
          } 
        } catch (e) {
          console.error('Failed to fetch SBC blob', e);
          return null;
        }
    
    });
  }

  generateSBCLinkWithAemPath() {
    const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getComponentStates().ZipCounty;
    const effectiveDateState: EffectiveDateStateStructure = this.reducerUtilityService.getReducerFromApplicationState('EffectiveDate');

    const hiosId = this.selectedPlan.attributes.HIOS_ID;
    const region = zipCountyState.county.region.toLowerCase();
    const effectiveDateYear = effectiveDateState?.effectiveDateYear;
    const effectiveDateForSBCFilename = effectiveDateYear + "0101";

    this.url = `/content/dam/highmark/en/healthco/shopx/plan-documents/${effectiveDateYear}/sbcs/${region}/individual/I_${hiosId}_${effectiveDateForSBCFilename}_SBC.pdf`;
  }

  async generateSBCLink(): Promise<Blob | null> {
    const effectiveDateState: EffectiveDateStateStructure = this.reducerUtilityService.getReducerFromApplicationState('EffectiveDate');
    const effectiveDateYear = effectiveDateState?.effectiveDateYear;
    // Pad HIOS_ID with two zeros
    const paddedHiosId = this.selectedPlan.attributes.HIOS_ID.padEnd(16, '0');
    const payload = {
      "payload": {
        "region": "WY", //This for the WY region If needed fro all we will update Dynamically
        "year": `${effectiveDateYear}`,
        "hiosID": paddedHiosId,
        "docType": "SBC"
      }
    };
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      strategyConfigs: { forceModernUrl: true },
      headers: new HttpHeaders({
      }),
      POST_OR_PUT_PAYLOAD: payload,
      url: "/sales/v1/ext/get-document",
      responseType: 'blob'
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS_V4);

    try {
      return await this.httpClient.makeRequest(requestConfig) as Blob;
    } catch (e) {
      console.error('Failed to fetch SBC blob', e);
      return null;
    }
  }

  private createBlobUrl(blob: Blob): string {
    const blobObj = new Blob([blob], { type: "application/pdf" });
    return URL.createObjectURL(blobObj);
  }
}
